const SAMPLE_RATE = 8000;
const BITS_PER_SAMPLE = 16;
const BYTES_PER_SAMPLE = BITS_PER_SAMPLE / 8;

// A gentle, looping 4-note arpeggio used as placeholder audio for tracks
// with no preview. Soft and low-volume by design — this is a stand-in cue,
// not content meant to be listened to.
const NOTE_HZ = [220.0, 277.18, 329.63, 440.0]; // A3, C#4, E4, A4
const NOTE_DURATION_SEC = 0.3;
const PATTERN_DURATION_SEC = NOTE_HZ.length * NOTE_DURATION_SEC;
const PEAK_AMPLITUDE = 0.32; // Fraction of full scale — soft, not jarring.
const DYNAMICS_FLOOR = 0.15; // Never fades to (near-)silence.

// Three slow oscillators at periods with no common factor with each other
// or with PATTERN_DURATION_SEC (1.2s), so the overall loudness wanders up
// and down across the whole track instead of repeating in lockstep with the
// arpeggio. This is what gives the rendered waveform real peaks and valleys
// instead of a flat, uniform loop.
function dynamicsEnvelope(tSec: number): number {
  const slow = Math.sin((2 * Math.PI * tSec) / 17.3);
  const mid = Math.sin((2 * Math.PI * tSec) / 6.1 + 1.7);
  const fast = Math.sin((2 * Math.PI * tSec) / 2.3 + 0.4);
  const combined = 0.5 + 0.28 * slow + 0.15 * mid + 0.09 * fast;
  return Math.min(1, Math.max(DYNAMICS_FLOOR, combined));
}

function sampleAt(tSec: number): number {
  const tInPattern = tSec % PATTERN_DURATION_SEC;
  const noteIndex = Math.floor(tInPattern / NOTE_DURATION_SEC);
  const tInNote = tInPattern - noteIndex * NOTE_DURATION_SEC;
  const freq = NOTE_HZ[noteIndex];
  // Smooth in/out envelope per note so the arpeggio doesn't click at note
  // boundaries. Phase uses absolute time, so pitch stays continuous too.
  const noteEnvelope = Math.sin((Math.PI * tInNote) / NOTE_DURATION_SEC);
  return (
    Math.sin(2 * Math.PI * freq * tSec) *
    noteEnvelope *
    dynamicsEnvelope(tSec) *
    PEAK_AMPLITUDE
  );
}

/**
 * Builds a soft, looping placeholder tone sized to `durationMs` and returns
 * an object URL for it. Used when a track has no preview audio (or its
 * preview fails to load) so the trim editor always has something real to
 * play and a waveform with actual shape, rather than a flat silent line.
 * Callers own the returned URL and must revoke it (`URL.revokeObjectURL`)
 * once it's no longer needed.
 */
export function createPlaceholderAudioUrl(durationMs: number): string {
  const totalSamples = Math.max(
    1,
    Math.ceil((SAMPLE_RATE * durationMs) / 1000),
  );
  const dataSize = totalSamples * BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * BYTES_PER_SAMPLE, true); // byte rate
  view.setUint16(32, BYTES_PER_SAMPLE, true); // block align
  view.setUint16(34, BITS_PER_SAMPLE, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  for (let i = 0; i < totalSamples; i++) {
    const value = sampleAt(i / SAMPLE_RATE);
    view.setInt16(44 + i * BYTES_PER_SAMPLE, Math.round(value * 32767), true);
  }

  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}
